import { Box, CardContent, Tab, Tabs, Theme, Tooltip } from '@mui/material';
import Card from '@mui/material/Card';
import { makeStyles } from 'tss-react/mui';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../app/store';
import useAgentInterception from '../../../hooks/agentInterception/useAgentInterception.hooks';
import useScopedCartActions from '../../../hooks/cartScope/useScopedCartActions.hooks';
import { cartItemsForScope } from '../../../redux/features/draftCart/cartScope.routing';
import { BLOCKED_AFFORDANCE_STYLE } from '../../../utils/agentModeAffordance';
import { ISelectModifierPayload } from '../../../reducers/cartSlice';
import { dialogActions } from '../../../reducers/dialogSlice';
import useItemConversion from '../../../hooks/useItemConversion.hooks';
import {
  AgentInterceptionType,
  messagingActions,
  TextFrameTransmissionMessage,
} from '../../../reducers/messagingSlice';
import {
  selectDialogSelectedModGroup,
  selectSelectedItemCartId,
} from '../../../selectors/dialog';
import {
  modSymbolMappingSelector,
  posSettingsSelector,
  selectIsExpressUpsellMode,
  selectIsExpressTimerActive,
} from '../../../selectors/menu';
import { selectIsAgentActionBlocked } from '../../../redux/features/agentMode/agentMode.selector';
import { selectWithheldConstraintNames } from '../../../redux/features/experiments/experiments.selector';
import { selectUnavailableItemsByName } from '../../../selectors/menu';
import {
  selectFeatureFlagSuppressUpsell,
  selectIsHitl7CenterPaneEnabled,
  selectIsRepeatedSlotPanelEnabled,
} from '../../../redux/features/config/config.selector';
import { SetCartItemRef } from '../../../types';
import { getItemUnavailableMessage } from '../../../utils/TTSMessage';
import logger from '../../../utils/logger';
import { CartItem } from '../../../utils/cart';
import Colors from '../../../utils/color';
import { useDisplayPreference } from '../../Layout/DisplayPreferenceContext';
import { sortModGroups } from '../../../utils/menu';
import ModGroup from './ModGroup';
import RepeatedSlotPanel from './RepeatedSlotPanel';
import {
  findRepeatedSlotSets,
  RepeatedSlotSet,
} from '../util/repeatedSlots.utils';
import { ModGroupOpenProvider } from './ModGroupOpenContext';
import TruncationTooltip from '../components/TruncationTooltip';
import { ParsedMenuItem, ParsedModifierGroup } from '../../../types/menu';
import useModifierSelectionInfo from '../../../hooks/useModifierSelectionInfo/useModifierSelectionInfo.hooks';
import { getCartItemDescription } from '../util/helper';
import { getMenuUnificationFeatureFlag } from '../../../utils/session-storage';
import { UPSELL, UPSELL_PREFIX } from '../../../constants';

const useStyles = makeStyles()((theme: Theme) => ({
  menuItemCard: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  // hitl_3_0 only: tighter vertical margins (8px instead of 16px).
  menuItemCardCompact: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  menuItemCardContent: {
    padding: '0px !important',
  },
  textFieldContainer: {
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      border: '1px solid rgba(0, 0, 0, 0.12)',
    },
  },
  tabsContainer: {
    backgroundColor: Colors.gallery,
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    marginBottom: theme.spacing(0.5),
    '& .MuiTab-root': {
      backgroundColor: Colors.porcelain,
    },
    '& .MuiButtonBase-root.MuiTab-root': {
      padding: `0 0 0 ${theme.spacing(1.25)}`,
      fontWeight: 'bold !important',
    },
    '& .MuiButtonBase-root.MuiTab-root.Mui-selected': {
      color: Colors.black,
      backgroundColor: Colors.white,
    },
  },
  menuItemTabPanel: {
    '& > div': {
      padding: '0 !important',
    },
  },
  tabPanelBox: {
    borderColor: 'rgba(0, 0, 0, 0.12)',
    padding: '0',
  },
  tabLabel: {
    color: '#78716c',
    backgroundColor: '#fffbeb',
    padding: '5px',
    fontWeight: 'normal',
    fontSize: 'small',
    textTransform: 'none',
    textAlign: 'left',
    wordBreak: 'break-word',
  },
  tabContent: {
    color: Colors.daintree,
    padding: '1rem',
    paddingBottom: '0rem',
    fontSize: '0.75rem',
    width: '100%',
    maxWidth: 'none',
  },
  // HITL-1616: with the flag on the tab header is ONE fixed-height line.
  // Ungated the item name is `flexShrink: 0` and the description wraps on word
  // boundaries, so in a narrow pane the name refuses to give ground, the
  // description is squeezed to a few pixels and breaks ONE CHARACTER PER LINE,
  // and the header grows to several hundred pixels tall.
  tabHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    // 24px matches CartItemReview.lineItemsWrapper, the Agent Cart line.
    height: 24,
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
  },
  // The Agent Cart's item name, rule for rule: CartItemReview.mainItemname is
  // 12px / bold / 1.57 line height. The tab's own uppercase goes with it, so
  // the name reads here the way it reads in the cart.
  tabItemName: {
    flex: '0 1 auto',
    minWidth: 0,
    // The Agent Cart's item heading is `CartItemReview.mainItemname`: a
    // `subtitle2` with exactly these three overrides. This label is a plain div
    // inside a MUI Tab, so it inherits `button` typography instead — and that
    // sets letterSpacing 0.02857em against subtitle2's 0.00714em, which
    // rendered the same name four times looser here than in the cart. Read off
    // the variant rather than hardcoded, so the two move together.
    letterSpacing: theme.typography.subtitle2.letterSpacing,
    fontSize: theme.spacing(1.5),
    fontWeight: 'bold',
    lineHeight: 1.57,
    // The cart paints the heading `Colors.daintree`; the Tab would otherwise
    // hand this `Colors.black` through its own `.Mui-selected` rule.
    color: Colors.daintree,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // Basis 0, so the description gives up its space before the name starts
  // shrinking. Replaces `tabLabel` outright rather than layering over it —
  // two classes of equal specificity would leave the padding a source-order
  // accident.
  tabItemDescription: {
    flex: '1 1 0',
    minWidth: 0,
    height: 24,
    lineHeight: '24px',
    color: '#78716c',
    backgroundColor: '#fffbeb',
    padding: '0 5px',
    fontWeight: 'normal',
    fontSize: 'small',
    textTransform: 'none',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // 4px instead of 10px: in a narrow pane every pixel to the left of the name
  // is one the name cannot use.
  tabsContainerCompact: {
    // MUI gives Tabs and Tab a 48px min-height each. The header row is 24px, so
    // without these the tab kept a 48px box and the other 24px showed as a band
    // of empty colour under the name.
    minHeight: 24,
    '& .MuiTabs-flexContainer': {
      minHeight: 24,
    },
    '& .MuiButtonBase-root.MuiTab-root': {
      minHeight: 24,
      padding: `0 0 0 ${theme.spacing(0.5)}`,
    },
  },
  // The converted-item badge, sized for the 24px header row. `convertedItemName`
  // below carries 2px of VERTICAL padding on top of its 1px borders, standing
  // the box at ~25px inside a 24px row — so `tabHeaderRow`'s overflow clipped
  // the bottom border clean off. Same box, horizontal padding only.
  //
  // A separate class rather than an override layered over `convertedItemName`:
  // two classes of equal specificity resolve by stylesheet order, which is not
  // something a later edit should be able to break by accident.
  tabItemNameConverted: {
    backgroundColor: '#e8f5e8',
    padding: '0 6px',
    borderRadius: '4px',
    border: '1px solid #4caf50',
    fontWeight: 'bold',
  },
  convertedItemName: {
    backgroundColor: '#e8f5e8', // Light green background for converted items
    padding: '2px 6px',
    borderRadius: '4px',
    border: '1px solid #4caf50', // Green border
    fontWeight: 'bold',
  },
}));

const TabPanel = ({
  children,
  index,
  value,
}: {
  children: React.ReactNode;
  index: number;
  value: number;
}) => {
  const { classes } = useStyles();
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`menu-item-choice-panel-${index}`}
      aria-labelledby={`menu-item-choice-panel-${index}`}
    >
      {value === index && (
        <Box className={classes.tabPanelBox}>
          <div className={classes.menuItemTabPanel}>{children}</div>
        </Box>
      )}
    </div>
  );
};

interface CartItemDetailProps {
  cartItem: CartItem;
  setCartItemRef: SetCartItemRef;
}

const CartItemDetail = ({ cartItem, setCartItemRef }: CartItemDetailProps) => {
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();
  const isHitl3 = useSelector(
    (state: RootState) => state.config.FEATURE_FLAG_HITL_3_0
  );
  const [tabValue, setTabValue] = useState(0);
  // HITL-1617: which cart this pane is editing. `main` with the flag off, and
  // `main` unless the agent has opened a My Cart line, so every legacy path
  // resolves to `cartActions` and `state.cart` exactly as before.
  const {
    scope: writeScope,
    actions: cartWriteActions,
    isDraft,
  } = useScopedCartActions();
  const { handleModifierSelection } = useItemConversion(writeScope);
  const cartItems = useSelector((state: RootState) =>
    cartItemsForScope(state, writeScope)
  );
  const modSymbolMapping = useSelector(modSymbolMappingSelector);
  const selectedModGroup = useSelector(selectDialogSelectedModGroup);
  const { selectedModGroupPath } = useSelector(
    (state: RootState) => state.dialog
  );
  const selectedItemCartId = useSelector(selectSelectedItemCartId);
  const posSettings = useSelector(posSettingsSelector);
  const unavailableItemsByName = useSelector(selectUnavailableItemsByName);
  const suppressUpsellEnabled = useSelector(selectFeatureFlagSuppressUpsell);
  const isCenterPaneEnabled = useSelector(selectIsHitl7CenterPaneEnabled);
  const isRepeatedSlotPanelEnabled = useSelector(
    selectIsRepeatedSlotPanelEnabled
  );
  // HITL-1696: CAPITALS reaches the item name too, so the header and the rows
  // under it read in one case. Inline and absent by default.
  const { pref: displayPref } = useDisplayPreference();
  const isCapsItemName = displayPref.cpCase === 'caps';
  const expressUpsellMode = useSelector(selectIsExpressUpsellMode);
  const expressTimerActive = useSelector(selectIsExpressTimerActive);
  const withheldConstraints = useSelector(selectWithheldConstraintNames);
  const { sendAgentInterception } = useAgentInterception();
  const isAgentActionBlocked = useSelector(selectIsAgentActionBlocked);
  const { sendModifierSelectionInfo } = useModifierSelectionInfo();
  const isUnifiedMenu = getMenuUnificationFeatureFlag();

  const sendAgentInterventionEvent = useCallback(
    (description?: string, type?: AgentInterceptionType) => {
      sendAgentInterception({
        data: {
          type: type || AgentInterceptionType.ORDER_CHANGED,
          description,
        },
      });
    },
    [sendAgentInterception]
  );

  const onTabChange = useCallback(
    (event: React.SyntheticEvent<Element, Event>, value: any) => {
      setTabValue(value);
    },
    []
  );

  /**
   * HITL-1606 — the sixth send choke point, and the one the story's first pass
   * missed.
   *
   * This file speaks to the customer WITHOUT going through `useTTSMessage`: it
   * dispatches `messagingActions.sendMessage` straight from here, for the
   * description tab (which then stamps `TTS_SENT` separately) and for the
   * out-of-stock line in `onModifierSelect` (which stamps nothing at all). So
   * the interception gate could not have caught either — the description tab
   * sends first and stamps after, and the out-of-stock line never stamps.
   *
   * Guarded at this local `sendMessage` rather than at its two callers, because
   * it is already the choke point for both, and any third caller added here
   * inherits the refusal instead of having to remember it.
   *
   * debug, not warn: the controls above are not disabled outside Intercept
   * (that is HITL-1613's pass), so a refusal here is the expected outcome, not
   * evidence of a missed surface.
   *
   * HITL-1612 adds a second gate at the middleware, on `sendMessage`'s wire
   * sink, which covers this path too. Both stay: the sink gate is the guarantee,
   * and refusing here as well keeps the refused frame out of `sentTextFrames`
   * at its origin rather than relying on one `if` three layers away.
   */
  const sendMessage = useCallback(
    (message: string) => {
      if (isAgentActionBlocked) {
        logger.debug({
          message:
            'Item-detail TTS refused: the agent is not in Intercept mode',
        });
        return;
      }
      const payload: Partial<TextFrameTransmissionMessage> = {
        data: { payload: message, status: 'final' },
      };
      dispatch(messagingActions.sendMessage(payload as any));
    },
    [dispatch, isAgentActionBlocked]
  );

  const descriptionToSend = getCartItemDescription(cartItem, isUnifiedMenu);

  // HITL-1613 gave this tab a "you cannot click me" affordance whenever its one
  // action — speaking the item description — would be swallowed. HITL-1617 adds
  // the second case: a My Cart line is the agent's own, and reading its
  // description to the customer would be describing something nobody ordered.
  // True in `Intercept` too, where the middleware TTS sink is open.
  const isTabClickRefused = isAgentActionBlocked || isDraft;

  const onTabClick = useCallback(
    (event: React.SyntheticEvent<Element, Event>) => {
      if (isTabClickRefused) return;
      if (descriptionToSend) {
        sendMessage(descriptionToSend);
        sendAgentInterventionEvent(
          `${cartItem.name} description is sent as TTS`,
          AgentInterceptionType.TTS_SENT
        );
      }
    },
    [
      cartItem.description,
      isTabClickRefused,
      sendMessage,
      sendAgentInterventionEvent,
    ]
  );

  // Default selections fire from every ModGroup's mount effect in the same
  // render wave — an item with a large constraint set (e.g. a Crowd Pleaser)
  // mounts dozens of groups at once, and one selectModifier dispatch per
  // default means one full store notification + render pass each (React 17
  // does not batch outside event handlers). Queue them and commit each wave
  // as a single selectModifiers dispatch (HITL-1422).
  const pendingDefaultSelectionsRef = useRef<ISelectModifierPayload[]>([]);
  const defaultSelectionFlushScheduledRef = useRef(false);

  const queueDefaultSelection = useCallback(
    (payload: ISelectModifierPayload) => {
      pendingDefaultSelectionsRef.current.push(payload);
      if (defaultSelectionFlushScheduledRef.current) return;
      defaultSelectionFlushScheduledRef.current = true;
      // A microtask runs after the whole effect wave has queued its defaults
      // but before the browser paints or the next React commit.
      queueMicrotask(() => {
        defaultSelectionFlushScheduledRef.current = false;
        const selections = pendingDefaultSelectionsRef.current;
        pendingDefaultSelectionsRef.current = [];
        if (selections.length) {
          dispatch(
            cartWriteActions.selectModifiers({
              selections: selections.map((selection) => ({ selection })),
            })
          );
        }
      });
    },
    [dispatch, cartWriteActions]
  );

  const onMenuItemModifierSelect = useCallback(
    (
      menuItem: ParsedMenuItem | undefined,
      modGroup: ParsedModifierGroup,
      pathToItem: string,
      selected: boolean,
      fromClick?: boolean,
      isDefaultSelection?: boolean
    ) => {
      if (!menuItem) return;

      // HITL-1612: the centre pane was A4's one missed write surface. Its picks
      // dispatch `selectModifier` / `deleteCartItem` and trigger the item
      // conversion below — all into the MAIN cart — and the cart-change effect
      // in `OrderReview` then sends an `order`. A4 refused the interception this
      // handler stamps further down, not the mutation, so in `Shadow` the event
      // was withheld while the AI's cart was quietly edited.
      //
      // Keyed on `!isDefaultSelection` rather than on `fromClick`: default
      // selections fire from every `ModGroup`'s mount effect (ModGroup.tsx:190,
      // `isDefaultSelection: true`) with no human involved, and must keep
      // applying so the AI's own items build correctly. Anything that is NOT an
      // automatic default is refused, so a future third caller fails closed.
      //
      // HITL-1617 narrows the refusal from "the agent is not in Intercept" to
      // "this write would land in the MAIN cart and the agent is not in
      // Intercept". The reason the pane was refused wholesale was that its only
      // write target was `cartSlice`; now that a My Cart line routes to
      // `draftCart`, building there in `Shadow` is the entire point of the mode
      // and nothing about it can reach the POS or the customer. A main-cart pick
      // in `Shadow` is refused exactly as it was — that is HITL-1606's
      // invariant, and it is untouched.
      if (isAgentActionBlocked && !isDraft && !isDefaultSelection) {
        logger.debug({
          message: `Centre-pane modifier "${menuItem.name}" refused: the agent is not in Intercept mode`,
        });
        return;
      }

      // Out-of-stock (86'd today) or meal-period inactive — both surface as
      // `available: false` in the parsed menu item.
      if (menuItem.available === false) {
        // HITL-1617: the pick is refused in both carts — an 86'd option is 86'd
        // whoever is building — but the customer is only told about the ORDER.
        // In `Intercept` the middleware's TTS sink is open, so without this the
        // drive-thru would hear "we're out of bacon" because the agent touched a
        // line in their own private draft.
        // HITL-1708: nor about a default the menu tried to apply on its own —
        // nobody asked for it, so there is nothing to tell the customer.
        if (!isDraft && !isDefaultSelection)
          sendMessage(getItemUnavailableMessage(menuItem.name));
        return;
      }

      // Same rule for the metric: a draft edit is not an order event. Emitting
      // it would put a modifier selection on the wire for an item that may never
      // be promoted, which is the `final_order_validation` mistake in miniature.
      if (!isDraft) sendModifierSelectionInfo({ menuItem, modGroup, selected });
      if (
        menuItem.name.toLocaleLowerCase() !== 'declined' &&
        !(
          menuItem.name.toLocaleLowerCase() === 'decline' &&
          (cartItem.name.startsWith(UPSELL_PREFIX) ||
            cartItem.name.startsWith(UPSELL))
        )
      ) {
        const selectionPayload: ISelectModifierPayload = {
          cartItemId: cartItem.cartItemId,
          menuItemId: menuItem.itemId,
          modGroupId: modGroup.id,
          selected,
          modCode: '',
          modSymbolMapping,
          pathToItem,
          posSettings,
          readback: true,
          applySuppressCascade: true,
          suppressUpsellEnabled,
          applyExpressCascade: expressUpsellMode || expressTimerActive,
          withheldConstraints,
        };
        if (isDefaultSelection) {
          queueDefaultSelection(selectionPayload);
        } else {
          dispatch(cartWriteActions.selectModifier(selectionPayload));
        }

        // Nothing in the draft has been ordered, so there is no order change to
        // announce. This also keeps the take-over honest: an interception is
        // what flips `isAIAutoMode` and stands the AI down for the rest of the
        // session, and editing a private draft must not be what does that.
        if (fromClick && !isDraft)
          sendAgentInterventionEvent(`${menuItem.name} added to order`);
      } else {
        dispatch(
          cartWriteActions.deleteCartItem(cartItem.cartItemId.toString())
        );
        if (fromClick && !isDraft)
          sendAgentInterventionEvent(`${cartItem.name} removed from order`);

        const cartItemIds = Object.keys(cartItems)
          .filter((id) => id !== cartItem.cartItemId.toString())
          .map((id) => parseInt(id));
        const newCartItemId = Math.max(...cartItemIds);
        if (newCartItemId > 0) {
          dispatch(
            dialogActions.selectItemAndResetNavigation({
              item: cartItems[newCartItemId.toString()],
              itemCartId: newCartItemId,
              // HITL-1617: `cartItems` is the scoped map, so the neighbour we
              // fall back to belongs to the cart we just deleted from. Without
              // the scope the id would be read back against `cartSlice` and open
              // the AI's item of the same number.
              cartScope: writeScope,
            })
          );
        } else {
          dispatch(dialogActions.selectItemAndResetNavigation(undefined));
        }
      }

      // Check for item conversion and option replacement AFTER processing the normal modifier selection
      // This ensures the agent intervention event is sent for the conversion size selection
      // Skip conversion logic for default selections to prevent duplicate processing
      if (!isDefaultSelection)
        handleModifierSelection(modGroup, menuItem.name, cartItem);
    },
    [
      cartItem,
      cartItems,
      dispatch,
      handleModifierSelection, // Added to dependencies
      // HITL-1612: must be a dependency. The ref wrapper below always points at
      // the latest callback, but the callback is only rebuilt when a dep
      // changes — so without this the guard would keep reading the mode from
      // whenever it was last memoised, and a Shadow -> Intercept switch
      // would go on refusing picks.
      isAgentActionBlocked,
      // HITL-1617: same reason as `isAgentActionBlocked` above — the callback is
      // only rebuilt when a dep changes, so without these a pane that switched
      // from an AI line to a My Cart line would go on writing to the old cart.
      isDraft,
      cartWriteActions,
      writeScope,
      modSymbolMapping,
      posSettings, // Added to dependencies
      queueDefaultSelection,
      sendAgentInterventionEvent,
      sendModifierSelectionInfo, // Added to dependencies
      sendMessage,
      suppressUpsellEnabled,
      expressUpsellMode,
      withheldConstraints,
    ]
  );

  // Identity-stable wrapper: onMenuItemModifierSelect recomputes whenever the
  // cart changes (it closes over cartItem/cartItems), and a changing callback
  // identity would defeat ModGroup's scoped memo comparator — re-rendering
  // every mounted group on each click. The ref always points at the latest
  // closure, so behavior is identical while the identity never changes.
  const onMenuItemModifierSelectRef = useRef(onMenuItemModifierSelect);
  onMenuItemModifierSelectRef.current = onMenuItemModifierSelect;
  const stableSelectMenuItemModifier = useCallback(
    (...args: Parameters<typeof onMenuItemModifierSelect>) =>
      onMenuItemModifierSelectRef.current(...args),
    []
  );

  const modifierGroups = sortModGroups(cartItem);

  // The repeated-slot panel takes the place of a set's rows: drawn where the
  // set's first slot would have been, the other slots skipped. With the flag
  // off this map is empty and the loop below renders exactly as before.
  //
  // Menu defaults still apply: the panel runs ModGroup's default logic per
  // slot (applyModGroupDefaults) through the same selection callback.
  const repeatedSlotSetByGroupId = useMemo(() => {
    const byGroupId = new Map<string, RepeatedSlotSet>();
    if (!isRepeatedSlotPanelEnabled) return byGroupId;
    findRepeatedSlotSets(modifierGroups).forEach((slotSet) =>
      slotSet.groups.forEach((group) =>
        byGroupId.set(String(group.id), slotSet)
      )
    );
    return byGroupId;
    // `modifierGroups` is rebuilt every render; the item's own group map is
    // what it is derived from.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRepeatedSlotPanelEnabled, cartItem.modifierGroups]);

  return (
    <Card
      className={cx(
        classes.menuItemCard,
        isHitl3 && classes.menuItemCardCompact
      )}
      elevation={3}
      sx={{ borderRadius: 1.5 }}
    >
      <CardContent className={classes.menuItemCardContent}>
        <Tabs
          value={tabValue}
          onClick={onTabClick}
          onChange={onTabChange}
          aria-label={cartItem.name}
          TabIndicatorProps={{ style: { display: 'none' } }}
          className={cx(
            classes.tabsContainer,
            isCenterPaneEnabled && classes.tabsContainerCompact
          )}
        >
          <Tab
            label={
              isCenterPaneEnabled ? (
                <div className={classes.tabHeaderRow}>
                  <TruncationTooltip title={cartItem.name}>
                    <div
                      className={cx(
                        classes.tabItemName,
                        cartItem.isConverted && classes.tabItemNameConverted
                      )}
                      style={
                        isCapsItemName
                          ? { textTransform: 'uppercase' }
                          : undefined
                      }
                    >
                      {cartItem.name}
                    </div>
                  </TruncationTooltip>
                  {/*
                    Only painted when there is something to paint. The amber is
                    the description's own background, so rendering the element
                    unconditionally drew a full-width empty bar beside every
                    item that has no description.
                  */}
                  {!!descriptionToSend && (
                    <TruncationTooltip title={descriptionToSend}>
                      <div className={classes.tabItemDescription}>
                        {descriptionToSend}
                      </div>
                    </TruncationTooltip>
                  )}
                </div>
              ) : (
                <div
                  style={{ display: 'flex', gap: '10px', alignItems: 'center' }}
                >
                  <div
                    style={{ flexShrink: 0, fontSize: '15px' }}
                    className={
                      cartItem.isConverted ? classes.convertedItemName : ''
                    }
                  >
                    {cartItem.name}{' '}
                  </div>
                  <div className={classes.tabLabel}>
                    {descriptionToSend || ''}
                  </div>
                </div>
              )
            }
            ref={(ref) => {
              setCartItemRef(cartItem.cartItemId, ref);
            }}
            sx={{ alignItems: 'start' }}
            className={classes.tabContent}
            // HITL-1613: this tab is a single tab, so the only thing a plain
            // click does is send the description as TTS (`onTabClick` below) —
            // which HITL-1612 refuses at the middleware sink outside Intercept.
            // ButtonBase paints `cursor: pointer` on it regardless, so the
            // affordance went on inviting a click that was swallowed.
            //
            // `style`, not `sx`: an `sx` key would change the emotion hash on
            // every render, flag off included, and this component is
            // snapshotted. `style` is simply absent when the mode is not silent.
            style={isTabClickRefused ? BLOCKED_AFFORDANCE_STYLE : undefined}
            // HITL-1532: this tab's label is the item name AND the item's whole
            // menu description, so an Alt+click read-out inferring the label
            // from `textContent` spoke the description too (and said nothing at
            // all once the two together passed the read-out's length cap). Name
            // the item name as the thing to speak. The description stays
            // reachable by its own gesture — a plain click still sends it as
            // TTS via `onTabClick`.
            data-speak-text={cartItem.name}
          />
        </Tabs>
        <TabPanel value={tabValue} index={0}>
          {/*
            HITL-1616: which option drawers the agent has opened, scoped to this
            item so opening one on a burger says nothing about the next item
            selected. The provider is inert with the flag off — nothing below
            reads it — so the legacy tree is unchanged.
          */}
          <ModGroupOpenProvider>
            {modifierGroups.map((modGroup: ParsedModifierGroup) => {
              const slotSet = repeatedSlotSetByGroupId.get(String(modGroup.id));
              if (slotSet) {
                return slotSet.groups[0].id === modGroup.id ? (
                  <RepeatedSlotPanel
                    key={`${slotSet.stem}_repeated_slots`}
                    cartItem={cartItem}
                    slotSet={slotSet}
                    selectMenuItemModifier={stableSelectMenuItemModifier}
                  />
                ) : null;
              }
              return (
                <ModGroup
                  key={`${modGroup.id}_display`}
                  cartItem={cartItem}
                  modGroup={modGroup}
                  selectMenuItemModifier={stableSelectMenuItemModifier}
                  pathToItem={modGroup.id}
                  isSelectedToFocus={
                    selectedModGroupPath === modGroup.id &&
                    selectedItemCartId === cartItem.cartItemId
                  }
                />
              );
            })}
          </ModGroupOpenProvider>
        </TabPanel>
      </CardContent>
    </Card>
  );
};

export default memo(CartItemDetail);
